import { Button, Chip, Stack, Tooltip, Typography } from '@mui/material'
import SendRoundedIcon from '@mui/icons-material/SendRounded'
import { IDataTableColumn } from '../../../components/Tables/DataTable'
import { TProduct } from '../../../core/types/Product'
import DataTableCellTypography from '../../../components/Typographys/DataTableCellTypography'
import Convertions from '../../../core/functions/convertions'
import { HtmlTooltip } from '../../../components/Tooltip/HtmlTooltip'
import { ProductSituationEnum } from '../../../core/types/Situation'
import ProductSituationBlockModal from './ProductSituationBlockModal'
import { Link } from 'react-router-dom'
import { TenantController } from '../../../core/controllers/TenantController'
import { TTenantSettings } from '../../../core/types/Tenant'
import { IErrorResponse } from '../../../core/types/ErrorResponse'
import { useAdminUserContext } from '../../../core/hooks/useAdminUserContext'
import { useContext } from 'react'
import { AuthContext } from '../../../core/context/AuthContext'
import { IconClone, IconEye } from '../../../core/constants/IconConsts'
import { MyProductsColumnsProp } from '../ProductTypes'
import { getStatusChipStyles } from '../ProductConsts'
import ConfirmDialog from '../../../components/Dialogs/ConfirmDialog'

const myProductsColumns = ({
  status,
  onOpenTenantSettings,
  onChangeClone,
  onResendAccess,
}: MyProductsColumnsProp): IDataTableColumn<TProduct>[] => {
  const { isUserSysAdmin } = useContext(AuthContext)
  const { setTenantSettings } = useAdminUserContext()

  const isAdmin = isUserSysAdmin()

  const handleChangeTenant = async (product: TProduct) => {
    const { tenantId } = product
    const result = await TenantController.getSettings({ id: tenantId || '' })
    if (result) {
      const resultError = result as IErrorResponse
      if (resultError.error) {
        return
      }
      const tenantSettings = result as TTenantSettings
      setTenantSettings(tenantSettings)
      onOpenTenantSettings()
    }
  }

  const handleChangeClone = async (product: TProduct) => {
    onChangeClone(product)
  }

  const columns: IDataTableColumn<TProduct>[] = [
    {
      id: 'name',
      label: 'Nome',
      width: isAdmin ? '50%' : '70%',
      render(id, value, row) {
        return (
          <Stack direction="row" alignItems="center">
            <img
              src={
                row.imageProductUrl
                  ? row.imageProductUrl
                  : '/assets/logo/logoCeletus.svg'
              }
              style={{
                width: 40,
                height: 40,
                marginRight: 10,
                borderRadius: 6.67,
                objectFit: 'cover',
                objectPosition: 'center',
              }}
            />
            <DataTableCellTypography>{value}</DataTableCellTypography>
            {Convertions.productTypePaymentToString(row.typePayment) ===
              'Assinatura' && (
              <HtmlTooltip title={<Typography>Recorrência</Typography>}>
                <img
                  src={'/assets/icons/arrow/roundArrow.svg'}
                  alt="icon-eyes"
                  style={{
                    width: '15px',
                    height: '15px',
                    marginLeft: '5px',
                  }}
                />
              </HtmlTooltip>
            )}
          </Stack>
        )
      },
    },
  ]

  if (isAdmin) {
    const tenantColumn: IDataTableColumn<TProduct> = {
      id: 'tenantName',
      label: 'Produtor',
      width: '20%',
      render(id, value, row) {
        return (
          <Button
            color="inherit"
            onClick={() => handleChangeTenant(row)}
            sx={{ textTransform: 'none' }}
          >
            {value}
          </Button>
        )
      },
    }
    columns.push(tenantColumn)
  }

  const otherColumns: IDataTableColumn<TProduct>[] = [
    {
      id: 'status',
      label: 'Situação',
      width: '12%',
      render(id, value, row) {
        return (
          <Stack direction="row" spacing={1} alignItems="center">
            <Chip
              label={Convertions.productStatusToString(value)}
              size="small"
              sx={getStatusChipStyles(value)}
            />
          </Stack>
        )
      },
    },
    {
      id: 'memberServiceId',
      label: 'Referência',
      width: '8%',
    },
    {
      id: 'action',
      label: 'Ações',
      width: '5%',
      render(id, value, row) {
        return (
          <Stack direction="row" spacing={0.5}>
            {row.situation === ProductSituationEnum.Blocked ? (
              <ProductSituationBlockModal row={row} />
            ) : (
              status !== 'deleted' && (
                <>
                  <Tooltip title="Editar o produto">
                    <Button
                      component={Link}
                      to={`/product/${row.id}`}
                      variant="text"
                      size="small"
                      sx={{
                        px: 1,
                        py: 0.5,
                        m: 0,
                        minWidth: 0,
                        borderRadius: '8px',
                      }}
                    >
                      <img
                        src={IconEye}
                        alt="action-eye"
                        style={{ fontSize: '1.2rem' }}
                      />
                    </Button>
                  </Tooltip>
                  <Tooltip title="Duplicar o produto">
                    <Button
                      variant="text"
                      size="small"
                      sx={{
                        px: 1,
                        py: 0.5,
                        m: 0,
                        minWidth: 0,
                        borderRadius: '8px',
                      }}
                      onClick={() => handleChangeClone(row)}
                    >
                      <img
                        src={IconClone}
                        alt="action-copy"
                        style={{ fontSize: '1.2rem' }}
                      />
                    </Button>
                  </Tooltip>
                  {isAdmin && (
                    <Tooltip title="Reenviar acessos">
                      <Button
                        variant="text"
                        size="small"
                        sx={{
                          px: 1,
                          py: 0.5,
                          m: 0,
                          minWidth: 0,
                          borderRadius: '8px',
                        }}
                        onClick={() => onResendAccess(row)}
                      >
                        <SendRoundedIcon
                          fontSize="small"
                          sx={{ fontSize: '1.2rem' }}
                        />
                      </Button>
                    </Tooltip>
                  )}
                </>
              )
            )}
          </Stack>
        )
      },
    },
  ]

  columns.push(...otherColumns)
  return columns
}

export default myProductsColumns
